import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = API_BASE_URL;

// Authoritative subscription lives in the `subscriptions` collection; only
// GET /subscription reflects it for the caller's own company (never the
// legacy company.subscription_plan/status compatibility projection).
function calculateDaysRemaining(trialEndsAt) {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24)));
}

function formatPlanMoney(plan, amount) {
  const formatted = Number(amount || 0).toLocaleString('en-MY');
  return plan.currency === 'MYR' ? `RM${formatted}` : `$${formatted}`;
}

const Subscriptions = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editCompany, setEditCompany] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [mySubscription, setMySubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, companiesRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`, { withCredentials: true }),
        user?.role === 'super_admin' 
          ? axios.get(`${API}/companies`, { withCredentials: true })
          : Promise.resolve({ data: [] })
      ]);
      setPlans(plansRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error(error);
    }
  }, [user?.role]);

  const fetchMySubscription = useCallback(async () => {
    if (user?.role !== 'company_admin') return;
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const { data } = await axios.get(`${API}/subscription`, { withCredentials: true });
      setMySubscription(data);
    } catch (error) {
      // Never fall back to legacy company.subscription_plan/status on failure.
      setMySubscription(null);
      setSubscriptionError(
        error?.response?.status === 404
          ? 'No subscription record was found for your company yet.'
          : 'Unable to load your subscription right now. Please try again later.'
      );
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchMySubscription();
  }, [fetchMySubscription]);

  const handleUpdateSub = async () => {
    try {
      await axios.patch(
        `${API}/companies/${editCompany.id}/subscription`,
        { subscription_plan: newPlan, subscription_status: 'active' },
        { withCredentials: true }
      );
      toast.success('Subscription updated');
      setEditCompany(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          Subscription Plans
        </h1>
        <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
          Choose the plan that works best for your business
        </p>
      </div>

      {/* Current Subscription (Company Admin Only) - authoritative state from GET /subscription */}
      {user?.role === 'company_admin' && (
        <Card className="border-border/40" data-testid="current-subscription-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Your Company Subscription</CardTitle>
            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
              This subscription belongs to your company and applies across all of your restaurants. It is not tied to any single restaurant.
            </p>
          </CardHeader>
          <CardContent>
            {subscriptionLoading && (
              <p className="text-sm text-muted-foreground" data-testid="subscription-loading">Loading your subscription...</p>
            )}
            {!subscriptionLoading && subscriptionError && (
              <div
                className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
                data-testid="subscription-error"
                role="alert"
              >
                {subscriptionError}
              </div>
            )}
            {!subscriptionLoading && !subscriptionError && mySubscription && (() => {
              const matchedPlan = plans.find((p) => (
                p.id === mySubscription.plan_id
                && (p.version == null || p.version === mySubscription.plan_version)
              ));
              const daysRemaining = calculateDaysRemaining(mySubscription.trial_ends_at);
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="subscription-details">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Plan</p>
                    <p className="font-semibold" data-testid="subscription-plan">
                      {matchedPlan?.name || mySubscription.plan_id}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Status</p>
                    <Badge
                      data-testid="subscription-status"
                      style={{ backgroundColor: mySubscription.status === 'active' || mySubscription.status === 'trialing' ? '#10B981' : '#EF4444', color: 'white' }}
                    >
                      {mySubscription.status}
                    </Badge>
                  </div>
                  {mySubscription.trial_started_at && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Trial Started</p>
                      <p data-testid="subscription-trial-start">{new Date(mySubscription.trial_started_at).toLocaleDateString()}</p>
                    </div>
                  )}
                  {mySubscription.trial_ends_at && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Trial Ends</p>
                      <p data-testid="subscription-trial-end">{new Date(mySubscription.trial_ends_at).toLocaleDateString()}</p>
                      {daysRemaining !== null && (
                        <p className="text-xs text-muted-foreground" data-testid="subscription-days-remaining">
                          {daysRemaining > 0 ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining` : 'Trial period has ended'}
                        </p>
                      )}
                    </div>
                  )}
                  {matchedPlan && (matchedPlan.restaurant_limit !== undefined || matchedPlan.order_limit !== undefined) && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Plan Limits</p>
                      <p className="text-sm" data-testid="subscription-limits">
                        {matchedPlan.custom_limits ? 'Custom limits' : <>
                          {matchedPlan.restaurant_limit == null ? 'Unlimited restaurants' : `${matchedPlan.restaurant_limit} restaurant(s)`}
                          {' · '}
                          {matchedPlan.order_limit == null ? 'Unlimited orders' : `${matchedPlan.order_limit} orders/month`}
                        </>}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan, i) => (
          <Card 
            key={plan.id} 
            className={`border-2 relative overflow-hidden ${plan.id === 'pro' ? 'border-[#E57200] shadow-lg' : 'border-border/40'}`}
            data-testid={`plan-${plan.id}`}
          >
            {plan.is_most_popular && (
              <div className="absolute top-0 end-0 bg-[#E57200] text-white px-3 py-1 text-xs font-bold rounded-bl-lg" style={{ fontFamily: 'Figtree, sans-serif' }}>
                MOST POPULAR
              </div>
            )}
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                {plan.id === 'enterprise' && <Crown className="h-5 w-5" style={{ color: plan.color }} />}
                {plan.id === 'pro' && <Sparkles className="h-5 w-5" style={{ color: plan.color }} />}
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif', color: plan.color }}>{plan.name}</CardTitle>
              </div>
              <div className="space-y-1">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.price_display || formatPlanMoney(plan, plan.price)}</span>
                  {plan.billing_interval && <span className="text-sm text-muted-foreground">/{plan.billing_interval}</span>}
                </div>
                <p className="text-xs text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {plan.id === 'trial' ? 'Free for 14 Days · No credit card required' : plan.annual_price_minor ? `${formatPlanMoney(plan, plan.annual_price_minor / 100)}/year · ${plan.commercial_metadata?.annual_savings_display}` : plan.contact_sales ? 'Custom quotation' : 'Monthly billing'}
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: plan.color }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {(plan.setup_fee_minor != null || plan.setup_fee_display) && (
                <p className="text-xs text-muted-foreground">
                  Setup fee: {plan.setup_fee_display || formatPlanMoney(plan, plan.setup_fee_minor / 100)}
                </p>
              )}
              <Button 
                className="w-full" 
                style={{ backgroundColor: plan.color, color: 'white' }}
                data-testid={`select-plan-${plan.id}`}
                disabled={user?.role === 'company_admin'}
                title={user?.role === 'company_admin' ? 'Plan changes are not available yet' : undefined}
              >
                {user?.role === 'company_admin' ? 'Coming soon' : (plan.id === 'trial' ? 'Start Free Trial' : 'Select Plan')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {plans[0]?.commercial_metadata && (
        <div className="space-y-2 text-sm text-muted-foreground" data-testid="subscription-commercial-notes">
          <p>{plans[0].commercial_metadata.meta_whatsapp_fee_disclosure}</p>
          <p>{plans[0].commercial_metadata.launch_offer}</p>
          <ul className="list-disc ps-5">
            {plans[0].commercial_metadata.add_ons?.map((addOn) => (
              <li key={addOn.id}>{addOn.name}: {formatPlanMoney({ currency: addOn.currency }, addOn.price_minor / 100)} {addOn.description}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Company Subscriptions Management (Super Admin Only) */}
      {user?.role === 'super_admin' && (
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Company Subscriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <Table data-testid="subs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Current Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No companies yet</TableCell></TableRow>
                ) : (
                  companies.map(c => (
                    <TableRow key={c.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: plans.find(p => p.id === c.subscription_plan)?.color || '#64748B', color: 'white' }}>
                          {c.subscription_plan}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge style={{ backgroundColor: c.subscription_status === 'active' ? '#10B981' : '#EF4444', color: 'white' }}>
                          {c.subscription_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {/* Disabled: current PATCH contract is incompatible with the backend and would 422; re-enable once Phase B repairs it. */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          disabled
                          title="Subscription changes are not available yet"
                          data-testid={`change-sub-${c.id}`}
                        >
                          Coming soon
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editCompany} onOpenChange={() => setEditCompany(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ fontFamily: 'Figtree, sans-serif' }}>Company: <strong>{editCompany?.name}</strong></p>
            <Select value={newPlan} onValueChange={setNewPlan}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name} - ${p.price}/mo</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={handleUpdateSub} style={{ backgroundColor: '#E57200' }} data-testid="save-sub-btn">Update Subscription</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
